// ============================================================================
// CONFIG API TYPES
// ============================================================================
// Types for Configuration Framework module (matches ConfigModule DTOs)

/**
 * Config Group (matches ConfigGroupDto.cs)
 */
export interface ConfigGroup {
  id: string;
  groupKey: string;
  displayName: string;
  description?: string;
  icon?: string;
  displayOrder: number;
  /**
   * Setup place that maintains the group (settings cleanup, crs-db tables/264); null = Policies
   * with no section.
   */
  setupArea?: ConfigSetupArea | null;
  statusId: string;
  statusCode: string;
  statusName?: string;
  statusColor?: string;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
}

export type ConfigSetupArea =
  | 'MODULES'
  | 'BOOKING'
  | 'FRONT_OFFICE'
  | 'NOTIFICATIONS'
  | 'TAX_BILLING'
  | 'APPROVALS'
  | 'PROPERTY_TERMS';

/** A module switch (MODULES group) and where it is on (matches ModuleSwitchDto.cs). */
export interface ModuleSwitch {
  configKey: string;
  displayName: string;
  systemOn: boolean;
  /** Active properties whose effective value (property over system) is on. */
  propertiesOn: string[];
  /** On for at least one active property, or on at system level when there is no property. */
  onAnywhere: boolean;
}

/** The module switch keys (crs-db fixes/098). */
export const MODULE_KEYS = {
  tax: 'tax_calculation_enabled',
  restaurant: 'dineops_module_enabled',
  fairbooks: 'fairbooks_posting_enabled',
} as const;

/**
 * Config Definition (matches ConfigDefinitionDto.cs)
 */
export interface ConfigDefinition {
  id: string;
  configKey: string;
  configGroupId: string;
  groupKey: string;
  groupDisplayName: string;
  displayName: string;
  description?: string;
  dataType: 'String' | 'Integer' | 'Decimal' | 'Boolean' | 'List';
  defaultValue?: string;
  minValue?: string;
  maxValue?: string;
  validationRule?: string;
  isRequired: boolean;
  isMultiValue: boolean;
  maxScopeLevel: number;
  isSystem: boolean;
  unitLabel?: string;
  displayOrder: number;
  statusId: string;
  statusCode: string;
  statusName?: string;
  statusColor?: string;
  createdAt: string;
  createdBy?: string;
  updatedAt?: string;
  updatedBy?: string;
  /** Raw JSON string from tbl_config_definition.meta_data. Carries optional editor hints (e.g. allowed_values for String dropdowns). */
  metaData?: string;
  totalCount: number;
}

/**
 * Config List Item — value with active/inactive status
 */
export interface ConfigListItem {
  value: string;
  isActive: boolean;
}

/**
 * Config Effective Value (matches ConfigEffectiveValueDto.cs)
 * Represents the resolved value of a config setting after scope cascade.
 */
export interface ConfigEffectiveValue {
  configKey: string;
  displayName: string;
  dataType: string;
  defaultValue?: string;
  effectiveValue?: string;
  resolvedFromScopeType?: string;
  resolvedFromScopeId?: string;
  resolvedScopeLevel?: number;
  isMultiValue: boolean;
  listValues?: ConfigListItem[];
  unitLabel?: string;
  minValue?: string;
  maxValue?: string;
  isRequired: boolean;
  maxScopeLevel: number;
}

/**
 * Config Scope (matches ConfigScopeDto.cs)
 */
export interface ConfigScope {
  id: string;
  scopeType: string;
  scopeId?: string;
  parentScopeId?: string;
  scopeLevel: number;
  displayName?: string;
  statusId: string;
  statusCode: string;
  statusName?: string;
  createdAt: string;
  createdBy?: string;
}

/**
 * Config Value Update — single item in an update request
 */
export interface ConfigValueUpdate {
  configKey: string;
  value?: string;
  listValues?: ConfigListItem[];
}

/**
 * Update Config Request (matches UpdateConfigRequest.cs)
 */
export interface UpdateConfigRequest {
  scopeType: string;
  scopeId?: string;
  values: ConfigValueUpdate[];
}

/**
 * Advance Policy — convenience type for booking form integration
 */
export interface AdvancePolicyConfig {
  advancePercent: number;
  advanceDueDays: number;
}

/** Resolved APPROVAL_RULES config for the booking-side approval flow. */
export interface ApprovalRulesConfig {
  /** Discount percentage that triggers approval (e.g. 20 = 20%). */
  highDiscountThreshold: number;
  /** Whether complimentary bookings require approval. */
  compApprovalRequired: boolean;
  /** Whether an E1 room-upgrade allocation routes the booking for approval. */
  roomUpgradeRequiresApproval: boolean;
  /**
   * Whether the high-discount-threshold rule routes bookings for approval at
   * all. Fail-safe TRUE when the key is missing — a missing definition must
   * never loosen approval controls. RATE_BELOW_MINIMUM and COMPLIMENTARY are
   * independent guards, unaffected by this switch.
   */
  discountApprovalRequired: boolean;
}

/**
 * Resolved BLOCKING_POLICY config for the BLOCKED-state lifecycle.
 *
 * Two-stage rule (option B):
 *   1. Save & Block / fresh Block always uses `blockDurationHours` as the
 *      INITIAL expiry, regardless of whether extensions are allowed.
 *   2. Edit Block Expiry (extensions) is gated by `blockExtensionAllowed`:
 *      - false → extensions rejected entirely; `maxBlockHoldHours` is ignored.
 *      - true  → each extension may add up to `extensionHours`, capped at
 *                `now + maxBlockHoldHours`, with at most `maxExtensions`
 *                extensions per booking.
 *
 * The UI must disable `maxExtensions`, `extensionHours`, and
 * `maxBlockHoldHours` when `blockExtensionAllowed = false` so the user
 * doesn't think they apply.
 */
export interface BlockingPolicyConfig {
  /** Initial expiry assigned on Block / Save & Block. Always applies. */
  blockDurationHours: number;
  /**
   * Maximum total hours from "now" at extension time. Only meaningful when
   * `blockExtensionAllowed = true` — ignored entirely otherwise.
   */
  maxBlockHoldHours: number;
  /** Whether a cashier can extend an existing BLOCKED expiry. */
  blockExtensionAllowed: boolean;
  /** Maximum number of times a single BLOCKED booking can be extended. */
  maxExtensions: number;
  /** Hours added per extension. */
  extensionHours: number;
}
